import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import useMediaQuery from '@material-ui/core/useMediaQuery'

import { AccountContext } from '@/contexts/account'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { setEmployeeCurrentHotel } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { EmployeeHotel } from '@/models/employee-hotel'
import { findHotelNameMatch, HotelNameMatch } from '@/utils/hotel-name-search'

// スマホ横向きでは左に開くサブメニューが画面に収まらず、hover も使えないため右端のパネルで表示する
const SP_LANDSCAPE_MEDIA_QUERY = '(orientation: landscape) and (max-height: 500px)'

type HotelListItem = { hotel: EmployeeHotel; match: HotelNameMatch | null }

export const HotelSwitcher: React.FC = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { account, employeeHotels } = useContext(AccountContext)
  const isSpLandscape = useMediaQuery(SP_LANDSCAPE_MEDIA_QUERY)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [keyword, setKeyword] = useState<string>('')
  const [isPointerOutsideWindow, setIsPointerOutsideWindow] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)
  // 切替 API の完了後はページごと再読み込みするため、連打で PATCH が重複しないよう処理中は受け付けない
  const isSwitchingRef = useRef<boolean>(false)

  const hotelItems = useMemo<HotelListItem[]>(() => {
    const items = employeeHotels.map(hotel => ({ hotel, match: findHotelNameMatch(hotel.name, keyword) }))
    return keyword.trim() ? items.filter(item => item.match) : items
  }, [employeeHotels, keyword])

  const isCurrentHotel = (hotel: EmployeeHotel) => String(hotel.id) === String(account?.hotel?.id)

  const open = () => setIsOpen(true)

  const close = () => {
    setIsOpen(false)
    setKeyword('')
  }

  // 開いたまま端末を回転させると表示形式が切り替わり、PC 表示は hover 前提で閉じられなくなるため閉じておく
  useEffect(() => {
    close()
  }, [isSpLandscape])

  // Windows では IME の変換候補ウィンドウがカーソル下に出るとページ外へ出た扱いになり、relatedTarget なしの mouseleave が発火する。
  // そこで閉じると全角入力のたびに閉じてしまうため、ページ上でカーソルが次に乗った要素で閉じるか判定する
  useEffect(() => {
    if (!isPointerOutsideWindow) return

    const onMouseOver = (event: MouseEvent) => {
      setIsPointerOutsideWindow(false)
      if (event.target instanceof Node && containerRef.current?.contains(event.target)) return
      close()
    }

    document.addEventListener('mouseover', onMouseOver)
    return () => document.removeEventListener('mouseover', onMouseOver)
  }, [isPointerOutsideWindow])

  const onMouseLeave = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.relatedTarget instanceof Node) {
      close()
      return
    }
    setIsPointerOutsideWindow(true)
  }

  const onClickHotel = async (hotel: EmployeeHotel) => {
    if (isCurrentHotel(hotel) || isSwitchingRef.current) return

    isSwitchingRef.current = true
    try {
      await setEmployeeCurrentHotel(hotel.id)

      window.location.replace('/')
    } catch (error) {
      isSwitchingRef.current = false
      errorHandler(error)
    }
  }

  if (employeeHotels.length <= 1) return null

  const hotelList = (
    <>
      <div css={[searchBoxStyle, isSpLandscape && spSearchBoxStyle]}>
        <svg
          className="search-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" />
        </svg>
        <input
          type="text"
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          placeholder={t('Search by hotel name')}
          aria-label={t('Search by hotel name')}
        />
        {keyword && (
          <button type="button" onClick={() => setKeyword('')} aria-label={t('Clear search')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
      </div>
      <div css={[hotelItemsStyle, isSpLandscape && spHotelItemsStyle]}>
        {hotelItems.map(({ hotel, match }) => (
          <button
            key={hotel.id}
            type="button"
            onClick={() => onClickHotel(hotel)}
            title={hotel.name}
            aria-current={isCurrentHotel(hotel) || undefined}
            className={isCurrentHotel(hotel) ? 'hotel-item hotel-item--current' : 'hotel-item'}
          >
            <HighlightedHotelName name={hotel.name} match={match} />
          </button>
        ))}
        {!hotelItems.length && (
          <div className="no-result">
            {/* React 側でエスケープされるため、i18next の二重エスケープで「&」が「&amp;」と表示されないよう無効化する */}
            {t('No hotels match {{keyword}}', { keyword: keyword.trim(), interpolation: { escapeValue: false } })}
          </div>
        )}
      </div>
    </>
  )

  return (
    <div
      ref={containerRef}
      css={hotelSwitcherStyle}
      onMouseEnter={isSpLandscape ? undefined : open}
      onMouseLeave={isSpLandscape ? undefined : onMouseLeave}
    >
      <div className="title" onClick={isSpLandscape ? open : undefined}>
        <img src={require('@/static/images/header/hotel.svg')} css={{ paddingRight: 12 }} />
        {t('switch hotel')}
        <img alt="icon_arrow_right" src={require('@/static/images/arrow_right_gray.svg')} css={{ marginLeft: 'auto' }} />
      </div>
      {isOpen &&
        (isSpLandscape ? (
          // ヘッダ（z-index: 102）の内側に置くとサイドメニュー（103）の下に潜り、背景で覆えないため body 直下に出す
          createPortal(
            <div css={spLayerStyle}>
              <div className="overlay" onClick={close} />
              <div css={[listContainerStyle, spPanelStyle]}>{hotelList}</div>
            </div>,
            document.body,
          )
        ) : (
          <div css={pcSubmenuStyle}>
            <div css={[listContainerStyle, pcSubmenuContainerStyle]}>{hotelList}</div>
          </div>
        ))}
    </div>
  )
}

const HighlightedHotelName: React.FC<{ name: string; match: HotelNameMatch | null }> = ({ name, match }) => {
  if (!match) return <>{name}</>

  return (
    <>
      {name.slice(0, match.start)}
      <mark>{name.slice(match.start, match.end)}</mark>
      {name.slice(match.end)}
    </>
  )
}

const hotelSwitcherStyle = css({
  position: 'relative',
  '.title': {
    textTransform: 'capitalize',
    display: 'flex',
    alignItems: 'center',
    padding: '8px 8px 8px 16px',
    fontSize: '12px',
    fontWeight: 'bold',
    cursor: 'pointer',
    color: '#676767',
    '&:hover': {
      background: '#f2f2f2',
    },
  },
})

// 仕様の幅 300px は見えているパネルの幅。外側の余白 4px を含めて配置する
const pcSubmenuStyle = css({
  position: 'absolute',
  left: '-308px',
  top: 0,
  width: 308,
  padding: 4,
  boxSizing: 'border-box',
})

const pcSubmenuContainerStyle = css({
  maxHeight: '460px',
  borderRadius: 5,
  boxShadow: '0px 3px 10px #0000003d',
})

const spLayerStyle = css({
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 103,
  '.overlay': {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    background: 'rgba(20, 20, 20, 0.4)',
  },
})

const spPanelStyle = css({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  width: 360,
  boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.18)',
})

// iOS Safari は 16px 未満の入力欄にフォーカスすると画面を拡大するため、スマホでは 16px にする
const spSearchBoxStyle = css({
  input: {
    fontSize: 16,
  },
})

// タッチ操作では hover で全文を確認できないため、省略せず折り返して全文を表示する
const spHotelItemsStyle = css({
  '.hotel-item': {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
})

const listContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: '#fff',
  overflow: 'hidden',
  cursor: 'default',
})

const searchBoxStyle = css({
  position: 'relative',
  flexShrink: 0,
  padding: 12,
  borderBottom: '1px solid #f2f2f2',
  '.search-icon': {
    position: 'absolute',
    left: 24,
    top: 24,
    color: '#777',
  },
  input: {
    width: '100%',
    height: 40,
    boxSizing: 'border-box',
    padding: '0 40px 0 36px',
    border: '1px solid #d6d6d6',
    borderRadius: 6,
    background: '#fafafa',
    fontSize: 12,
    color: '#272727',
    '&:focus': {
      outline: '2px solid #f2a40b',
      outlineOffset: -1,
      borderColor: 'transparent',
    },
  },
  button: {
    position: 'absolute',
    right: 14,
    top: 14,
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 0,
    background: 'transparent',
    color: '#777',
    cursor: 'pointer',
  },
})

const hotelItemsStyle = css({
  flexGrow: 1,
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '3px',
    '&:hover': {
      background: '#a8a8a8',
    },
  },
  '.hotel-item': {
    display: 'block',
    width: '100%',
    padding: 16,
    border: 0,
    background: '#fff',
    textAlign: 'left',
    fontFamily: 'inherit',
    borderBottom: '1px solid #f2f2f2',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#676767',
    whiteSpace: 'nowrap', // テキストの改行を防ぐ
    overflow: 'hidden',
    textOverflow: 'ellipsis', // 長いテキストを省略表示
    cursor: 'pointer',
    '&:hover': {
      background: '#f2f2f2',
    },
    mark: {
      background: '#ffd999',
      color: 'inherit',
      fontStyle: 'normal',
      borderRadius: 2,
    },
  },
  // スタッフ選択と同じく、選択中は色で示して操作対象から外す
  '.hotel-item--current': {
    color: '#f2a40b',
    cursor: 'default',
    '&:hover': {
      background: 'transparent',
    },
  },
  '.no-result': {
    padding: '32px 16px',
    textAlign: 'center',
    fontSize: '12px',
    color: '#676767',
  },
})
